// src/pages/Enseignants/Events/EventsPage.jsx
import React, { useState, useEffect } from "react";
import { useUser } from "./../../Home/UserContext";
import { getRoleFromRoleId } from "./../../../utils/roleHelper";
import {
  getAllEvents,
  deleteEvent,
  getAdherentsByEvent,
  toggleEventActive,
  updateEventImage,
  uploadEventsFile,
  toggleEventRegistration,
} from "./../../../services/eventServices";
import { getSettings } from "./../../../services/settingsServices";
import DashboardNavigation from "./../../DashboardNavigation/DashboardNavigation";
import Button from "./../../../components/Button/Button";
import ButtonEdit from "./../../../components/Button/ButtonEdit";
import ButtonDelete from "./../../../components/Button/ButtonDelete";
import {
  FaPlusSquare,
  FaInfoCircle,
  FaSave,
  FaTimes,
  FaEnvelope,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaImage,
  FaDownload,
} from "react-icons/fa";
import formatDate from "./../../../utils/formatDate";
import CreateEventModal from "./CreateEventModal";
import EditEventModal from "./EditEventModal";
import UploadEventsModal from "./UploadEventsModal";
import EventDetailsModalEnseignants from "./EventDetailsModalEnseignants";
import useScrollToTop from "./../../../hooks/useScollToTop";
import ScrollIndicator from "./../../Home/ScrollIndicator";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import InfoBubble from "../../../components/InfoBubble/InfoBubble";
import GroupMessageModal from "./GroupMessageModal";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

// ✅ absolutisation des images d'événements
import { toAbsoluteEventPhotoUrl } from "../../../services/eventPhotoServices";

/* Petit switch accessible pour l’affichage public */
function VisibilitySwitch({
  checked,
  onChange,
  titleOn = "Affiché",
  titleOff = "Masqué",
  onBgClass = "bg-emerald-500", // ✅ NEW (couleur "ON")
  disabled = false, // ✅ NEW
}) {
  return (
    <button
      type="button"
      onClick={() => {
        if (!disabled) onChange?.();
      }}
      disabled={disabled}
      role="switch"
      aria-checked={checked}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition
        ${checked ? onBgClass : "bg-slate-300 dark:bg-slate-700"}
        ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
      title={disabled ? "Action indisponible" : checked ? titleOn : titleOff}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition
          ${checked ? "translate-x-5" : "translate-x-1"}`}
      />
    </button>
  );
}

const EventsPage = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [events, setEvents] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [editableEvent, setEditableEvent] = useState(null);
  const [detailsEvent, setDetailsEvent] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [adherents, setAdherents] = useState([]);
  const [filter, setFilter] = useState("upcoming");
  const [typeFilter, setTypeFilter] = useState("All");
  const [levelFilter, setLevelFilter] = useState("All");
  const [cateFilter, setCateFilter] = useState("All");
  const [sortAscending, setSortAscending] = useState(true);
  const [shortnameClub, setShortnameClub] = useState("");
  const [isGroupMsgOpen, setIsGroupMsgOpen] = useState(false);
  const [eventForGroupMsg, setEventForGroupMsg] = useState(null);

  // 🔎 Recherche + pagination + chargeur
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  // Upload image par ligne
  const [uploadingIds, setUploadingIds] = useState({}); // { [eventId]: true/false }

  // 🖼️ Preview plein écran
  const [previewSrc, setPreviewSrc] = useState("");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Reset pagination quand filtres/champs changent
  useEffect(() => {
    setPage(1);
  }, [filter, typeFilter, levelFilter, cateFilter, search, sortAscending]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await getSettings();
        if (data.length > 0) {
          setShortnameClub(data[0].shortname_club);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres", error);
        toast.error("Erreur lors de la récupération des paramètres.");
      }
    };

    fetchSettings();
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const data = await getAllEvents();
      setEvents(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors du chargement des événements", error);
      toast.error("Impossible de charger les événements.");
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (event) => {
    setEditableEvent(event);
    setIsEditModalOpen(true);
  };
  const closeEditModal = () => setIsEditModalOpen(false);

  const openUploadModal = () => setIsUploadModalOpen(true);
  const closeUploadModal = () => {
    setSelectedFile(null);
    setIsUploadModalOpen(false);
  };

  const openGroupMsgModal = (event) => {
    setEventForGroupMsg(event);
    setIsGroupMsgOpen(true);
  };
  const closeGroupMsgModal = () => {
    setIsGroupMsgOpen(false);
    setEventForGroupMsg(null);
  };

  const handleFileChange = (e) => setSelectedFile(e.target.files[0]);

  const handleUploadCalendarSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error("Veuillez sélectionner un fichier.");
      return;
    }
    setIsUploading(true);
    try {
      await uploadEventsFile(selectedFile);
      toast.success("Calendrier des événements uploadé avec succès !");
      closeUploadModal();
      fetchEvents();
    } catch (error) {
      console.error("Erreur lors de l'upload du calendrier :", error);
      toast.error("Erreur lors de l'upload du calendrier.");
    } finally {
      setIsUploading(false);
    }
  };

  const openDetailsModal = async (event) => {
    try {
      const adherentsData = await getAdherentsByEvent(event.id);
      setAdherents(adherentsData);
      setDetailsEvent(event);
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des adhérents et des coachs",
        error,
      );
      toast.error("Erreur lors de la récupération des données.");
    }
  };
  const closeDetailsModal = () => setDetailsEvent(null);

  const deleteEventFunc = async (id) => {
    try {
      await deleteEvent(id);
      setEvents((prev) => prev.filter((e) => e.id !== id));
      toast.success("Événement supprimé avec succès.");
    } catch (error) {
      console.error("Erreur lors de la suppression de l'événement", error);
      toast.error("Suppression impossible.");
    }
  };

  const isPastDate = (date) =>
    new Date(date).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0);

  // Listes
  const typeEvents = [
    shortnameClub || "Votre Club",
    "Tournoi Interclubs",
    "Cours commun club",
    "Stage Club",
    "Compétition",
    "Animation",
    "Stage & Regroupement Technique",
    "Passage de Grade",
    "Entraînement Départemental",
    "Stage Sportif Départemental",
    "Autre",
  ];
  const levelEvents = [
    "Club",
    "Départemental",
    "Régional",
    "National",
    "International",
  ];
  const cateEvents = [
    "Eveil",
    "Mini-Poussin(e)",
    "Poussin(e)",
    "Benjamin(e)",
    "Minime",
    "Cadet(te)",
    "Junior",
    "Sénior",
    "Vétéran",
  ];

  const handleToggleActive = async (event) => {
    try {
      const updatedEvent = await toggleEventActive(event.id);
      setEvents((prev) =>
        prev.map((e) =>
          e.id === event.id ? { ...e, is_active: updatedEvent.is_active } : e,
        ),
      );
    } catch (error) {
      console.error(
        "Erreur lors du changement de l'état de l'événement",
        error,
      );
      toast.error("Erreur lors du changement de l'état de l'événement.");
    }
  };

  const handleToggleRegister = async (ev) => {
    if (!ev?.id) return;

    const current = Number(ev?.can_register ?? 1); // default 1
    const next = current ? 0 : 1;

    // ✅ Optimistic UI
    setEvents((prev) =>
      prev.map((e) => (e.id === ev.id ? { ...e, can_register: next } : e)),
    );

    try {
      const res = await toggleEventRegistration(ev.id);

      // ✅ Supporte plusieurs shapes + fallback sur next si retour vide
      const newVal = Number(
        res?.can_register ?? res?.event?.can_register ?? next,
      );

      setEvents((prev) =>
        prev.map((e) => (e.id === ev.id ? { ...e, can_register: newVal } : e)),
      );

      // Si ton API ne renvoie rien, on peut refresh la liste
      if (res == null) {
        await fetchEvents();
      }

      toast.success(newVal ? "Inscriptions ouvertes." : "Inscriptions closes.");
    } catch (error) {
      console.error("Erreur lors du toggle can_register", error);

      // rollback optimistic
      setEvents((prev) =>
        prev.map((e) => (e.id === ev.id ? { ...e, can_register: current } : e)),
      );

      toast.error("Erreur lors du changement du statut d’inscription.");
    }
  };

  // Filtrage + recherche
  const filteredEvents = events.filter((event) => {
    const eventDate = new Date(event.start).setHours(0, 0, 0, 0);
    const now = new Date().setHours(0, 0, 0, 0);
    const categories = event.cate_event ? event.cate_event.split(",") : [];

    const inDate =
      filter === "all" ||
      (filter === "upcoming" && eventDate >= now) ||
      (filter === "past" && eventDate < now);

    const inType = typeFilter === "All" || event.type_event === typeFilter;
    const inLevel = levelFilter === "All" || event.level_event === levelFilter;
    const inCate = cateFilter === "All" || categories.includes(cateFilter);

    const q = search.trim().toLowerCase();
    const inSearch =
      q.length === 0 ||
      [event.titre, event.type_event, event.level_event]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));

    return inDate && inType && inLevel && inCate && inSearch;
  });

  const sortedEvents = filteredEvents.sort((a, b) => {
    const da = new Date(a.start).getTime();
    const db = new Date(b.start).getTime();
    return sortAscending ? da - db : db - da;
  });

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sortedEvents.length / PAGE_SIZE));
  const startIdx = (page - 1) * PAGE_SIZE;
  const paginatedEvents = sortedEvents.slice(startIdx, startIdx + PAGE_SIZE);

  // --- Colonnes d'export (même ordre que l'upload) ---
  const EXPORT_COLUMNS = [
    "id",
    "titre",
    "description",
    "start",
    "end",
    "type_event",
    "level_event",
    "cate_event",
    "lieu_event",
    "horaire_event",
    "photo_url",
    "agenda_url",
    "is_active",
  ];

  // Retourne un objet Date valide… ou undefined (cellule réellement vide dans XLSX)
  const asExcelDateOrEmpty = (d) => {
    if (!d) return undefined;
    const dt = new Date(d);
    if (isNaN(dt)) return undefined;
    // Par sécurité, on évite les dates "sentinelles" (ex: 1899/1900 venant d'un 0)
    if (dt.getFullYear() < 1901) return undefined;
    return dt;
  };

  // --- Export XLSX (fallback CSV) pour tous les événements filtrés & triés ---
  const handleExportFiltered = async () => {
    try {
      const rows = (sortedEvents || []).map((ev) => ({
        id: ev?.id ?? "",
        titre: ev?.titre ?? "",
        description: ev?.description ?? "",
        start: asExcelDateOrEmpty(ev?.start), // <= Date valide ou undefined
        end: asExcelDateOrEmpty(ev?.end), // <= Date valide ou undefined (PAS "")
        type_event: ev?.type_event ?? "",
        level_event: ev?.level_event ?? "",
        cate_event: ev?.cate_event ?? "",
        lieu_event: ev?.lieu_event ?? "",
        horaire_event: ev?.horaire_event ?? "",
        photo_url: ev?.photo_url ?? "",
        agenda_url: ev?.agenda_url ?? "",
        is_active: String(!!ev?.is_active), // "true"/"false"
      }));

      if (!rows.length) {
        toast.info("Aucun événement à exporter.");
        return;
      }

      // Try XLSX first
      try {
        const XLSX = await import("xlsx");
        const ws = XLSX.utils.json_to_sheet(rows, {
          header: EXPORT_COLUMNS,
          cellDates: true,
        });
        // Largeurs de colonnes lisibles
        ws["!cols"] = [
          { wch: 6 }, // id
          { wch: 28 }, // titre
          { wch: 40 }, // description
          { wch: 12 }, // start
          { wch: 12 }, // end
          { wch: 20 }, // type_event
          { wch: 16 }, // level_event
          { wch: 30 }, // cate_event
          { wch: 16 }, // lieu_event
          { wch: 30 }, // horaire_event
          { wch: 38 }, // photo_url
          { wch: 38 }, // agenda_url
          { wch: 10 }, // is_active
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "evenements_filtres");
        const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });

        const blob = new Blob([wbout], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const d = new Date();
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        a.href = url;
        a.download = `evenements_filtres_${yyyy}-${mm}-${dd}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Export XLSX généré.");
        return;
      } catch (errXlsx) {
        console.warn("xlsx indisponible, fallback CSV…", errXlsx);
      }

      // Fallback CSV (UTF-8 BOM)
      const csvHeaders = EXPORT_COLUMNS.join(",");
      const toCsvVal = (v) => {
        if (v instanceof Date && !isNaN(v)) {
          // format YYYY-MM-DD pour un CSV “propre”
          const yyyy = v.getFullYear();
          const mm = String(v.getMonth() + 1).padStart(2, "0");
          const dd = String(v.getDate()).padStart(2, "0");
          return `${yyyy}-${mm}-${dd}`;
        }
        const s = String(v ?? "");
        return `"${s.replace(/"/g, '""')}"`;
      };
      const csvBody = rows
        .map((r) => EXPORT_COLUMNS.map((k) => toCsvVal(r[k])).join(","))
        .join("\n");
      const csv = `\ufeff${csvHeaders}\n${csvBody}\n`;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const d = new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      a.href = url;
      a.download = `evenements_filtres_${yyyy}-${mm}-${dd}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.info("Export CSV généré (XLSX indisponible).");
    } catch (e) {
      console.error(e);
      toast.error("Échec de l’export des événements filtrés.");
    }
  };

  // Upload photo par ligne (conserve ta route/service existant)
  const handleRowImageChange = async (evId, file) => {
    if (!file) return;
    try {
      setUploadingIds((m) => ({ ...m, [evId]: true }));
      const updated = await updateEventImage(evId, file);
      if (updated?.photo_url) {
        setEvents((prev) =>
          prev.map((e) =>
            e.id === evId ? { ...e, photo_url: updated.photo_url } : e,
          ),
        );
      } else {
        fetchEvents();
      }
      toast.success("Photo mise à jour.");
    } catch (e) {
      console.error(e);
      toast.error("Échec de l’upload de la photo.");
    } finally {
      setUploadingIds((m) => ({ ...m, [evId]: false }));
    }
  };

  // ✅ Preview plein écran
  const openPreview = (src) => {
    setPreviewSrc(src);
    setIsPreviewOpen(true);
  };
  const closePreview = () => {
    setIsPreviewOpen(false);
    setPreviewSrc("");
  };

  return (
    <section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />
      <ScrollIndicator />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
        <HeaderTitle
          role="Enseignant"
          title="Événements"
          align="center"
          className="mb-1"
        />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-4">
          {/* Bandeau dégradé + actions */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 text-white">
              <h2 className="text-lg sm:text-xl font-semibold text-center">
                Gestion des événements du club
              </h2>
              <p className="text-sm/6 opacity-95 max-w-3xl mx-auto text-center mt-1">
                Recherchez, filtrez, triez et gérez l’affichage public. Invitez
                facilement un groupe à participer.
              </p>

              <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
                <Button
                  text="Ajouter un événement"
                  icon={FaPlusSquare}
                  onClick={() => setIsCreateModalOpen(true)}
                  className="!rounded-xl !shadow-md gap-2"
                />
                <Button
                  text="Uploader un calendrier"
                  icon={FaPlusSquare}
                  onClick={() => setIsUploadModalOpen(true)}
                  className="!rounded-xl !shadow-md gap-2"
                />
                <Button
                  text="Exporter événements filtrés"
                  icon={FaDownload}
                  onClick={handleExportFiltered}
                  className="!rounded-xl !shadow-md gap-2"
                />
              </div>
            </div>
          </div>

          {/* Filtres */}
          <div className="px-5 py-5 sm:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 items-end">
              {/* Recherche */}
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Recherche (titre, type, niveau)
                </label>
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Ex : Compétition, Club, Départemental…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full rounded-xl pl-9 pr-3 py-2 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>

              {/* Statut */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Statut
                </label>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                >
                  <option value="all">Tous</option>
                  <option value="upcoming">À venir</option>
                  <option value="past">Passés</option>
                </select>
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Type
                </label>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                >
                  <option value="All">Tous</option>
                  {typeEvents.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              {/* Niveau */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Niveau
                </label>
                <select
                  value={levelFilter}
                  onChange={(e) => setLevelFilter(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                >
                  <option value="All">Tous</option>
                  {levelEvents.map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </select>
              </div>

              {/* Catégorie + Tri */}
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Catégorie
                  </label>
                  <select
                    value={cateFilter}
                    onChange={(e) => setCateFilter(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  >
                    <option value="All">Toutes</option>
                    {cateEvents.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Tri rapide par date */}
                <button
                  type="button"
                  onClick={() => setSortAscending((s) => !s)}
                  className="btn btn-sm rounded-xl border border-slate-300 dark:border-slate-700"
                  title="Trier par date"
                >
                  {sortAscending ? "Date ⬇️" : "Date ⬆️"}
                </button>
              </div>
            </div>

            {/* Compteur */}
            <div className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-200">
              Événements affichés : {sortedEvents.length} / {events.length}
            </div>
          </div>

          {/* Contenu : Table desktop / Cards mobile */}
          <div className="px-5 pb-6 sm:px-8">
            {loading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-24"
                  />
                ))}
              </div>
            ) : sortedEvents.length === 0 ? (
              <div className="text-center py-12 text-slate-600 dark:text-slate-400">
                Aucun événement.
              </div>
            ) : (
              <>
                {/* Barre pagination (haut) */}
                <div className="flex items-center justify-between w-full px-1 pb-3 text-sm">
                  <span className="opacity-70">
                    {sortedEvents.length} événement
                    {sortedEvents.length > 1 ? "s" : ""}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={page === 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      title="Page précédente"
                    >
                      <FaChevronLeft />
                    </button>
                    <span className="px-2">
                      Page {page} / {totalPages}
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={page === totalPages}
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                      title="Page suivante"
                    >
                      <FaChevronRight />
                    </button>
                  </div>
                </div>

                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white/90 dark:bg-slate-900/40">
                  <table className="min-w-[960px] w-full text-sm">
                    <thead className="bg-slate-50/90 dark:bg-slate-900/70">
                      <tr className="text-left text-slate-700 dark:text-slate-200">
                        <th className="px-4 py-3">Affichage</th>
                        <th className="px-4 py-3">Inscriptions</th>
                        <th className="px-4 py-3">Photo</th>
                        <th className="px-4 py-3">Événement</th>
                        <th className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            Date
                            <button
                              type="button"
                              className="text-xs underline"
                              onClick={() => setSortAscending((s) => !s)}
                              title="Trier par date"
                            >
                              {sortAscending ? "⬇️" : "⬆️"}
                            </button>
                          </div>
                        </th>
                        <th className="px-4 py-3 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                      {paginatedEvents.map((event) => {
                        const past = isPastDate(event.start);
                        const imgSrc =
                          toAbsoluteEventPhotoUrl(event.photo_url) ||
                          "/img/no-image.jpg";
                        return (
                          <tr
                            key={event.id}
                            className="bg-white/90 dark:bg-slate-900/40 hover:bg-slate-50 dark:hover:bg-slate-900/60 transition-colors"
                          >
                            {/* Affichage */}
                            <td className="px-4 py-3">
                              <VisibilitySwitch
                                checked={!!event.is_active}
                                onChange={() => handleToggleActive(event)}
                                titleOn="Affiché sur le site"
                                titleOff="Masqué du site"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <VisibilitySwitch
                                checked={!!Number(event.can_register ?? 1)}
                                onChange={() => handleToggleRegister(event)}
                                titleOn="Inscriptions ouvertes"
                                titleOff="Inscriptions closes"
                                onBgClass="bg-blue-600" // ✅ couleur différente
                                disabled={past} // ✅ optionnel : on désactive si événement passé
                              />
                            </td>

                            {/* Photo (cliquable => preview) + action changer séparée */}
                            <td className="px-4 py-3">
                              <div className="w-[180px]">
                                <img
                                  src={imgSrc}
                                  alt={event.titre || "Illustration"}
                                  className="w-full h-auto object-cover rounded ring-1 ring-slate-200 dark:ring-slate-700 cursor-zoom-in"
                                  onClick={() => openPreview(imgSrc)}
                                  onError={(e) =>
                                    (e.currentTarget.src = "/img/no-image.jpg")
                                  }
                                />

                                {/* Action changer séparée */}
                                {/* <div className="mt-2 flex items-center gap-3">
                                  <label
                                    htmlFor={`file-${event.id}`}
                                    className="inline-flex items-center gap-2 text-xs underline cursor-pointer"
                                    title="Changer la photo"
                                  >
                                    <FaImage /> Changer la photo
                                  </label>
                                  {uploadingIds[event.id] && (
                                    <span className="text-[11px] opacity-70">
                                      Upload…
                                    </span>
                                  )}
                                </div>
                                <input
                                  id={`file-${event.id}`}
                                  type="file"
                                  accept="image/*"
                                  className="sr-only"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file)
                                      handleRowImageChange(event.id, file);
                                  }}
                                /> */}
                              </div>
                            </td>

                            {/* Infos */}
                            <td className="px-4 py-3">
                              <div className="font-semibold text-slate-900 dark:text-slate-100">
                                {event.titre || "—"}
                              </div>
                              <div className="text-xs opacity-80">
                                {event.type_event} • {event.level_event}
                              </div>
                              {event.cate_event && (
                                <div className="text-[11px] mt-1 italic opacity-70">
                                  {event.cate_event}
                                </div>
                              )}
                            </td>

                            {/* Date */}
                            <td className="px-4 py-3 text-slate-800 dark:text-slate-100">
                              {event.start ? formatDate(event.start) : "—"}
                              {past && (
                                <span className="ml-2 text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
                                  Passé
                                </span>
                              )}
                            </td>

                            {/* Actions */}
                            <td className="px-4 py-3">
                              <div className="flex justify-center items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => openDetailsModal(event)}
                                  className="w-8 h-8 flex items-center justify-center text-white rounded-md bg-blue-600 p-2 shadow-sm transition duration-300 ease-in-out hover:bg-blue-700"
                                  title="Détails & participants"
                                >
                                  <FaInfoCircle />
                                </button>

                                <button
                                  type="button"
                                  onClick={() => openGroupMsgModal(event)}
                                  disabled={past}
                                  className={`w-8 h-8 flex items-center justify-center text-white rounded-md p-2 shadow-sm transition duration-300 ease-in-out ${
                                    past
                                      ? "bg-slate-400 cursor-not-allowed"
                                      : "bg-green-600 hover:bg-green-700"
                                  }`}
                                  title={
                                    past
                                      ? "Événement passé"
                                      : "Inviter un groupe"
                                  }
                                >
                                  <FaEnvelope />
                                </button>

                                <ButtonEdit
                                  onClick={() => openEditModal(event)}
                                />
                                <ButtonDelete
                                  onClick={() => deleteEventFunc(event.id)}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards */}
                <div className="md:hidden space-y-3">
                  {paginatedEvents.map((event) => {
                    const past = isPastDate(event.start);
                    const imgSrc =
                      toAbsoluteEventPhotoUrl(event.photo_url) ||
                      "/img/no-image.jpg";
                    return (
                      <div
                        key={event.id}
                        className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-white/90 dark:bg-slate-900/40"
                      >
                        <div className="flex items-center gap-3">
                          <img
                            src={imgSrc}
                            alt={event.titre}
                            className="h-12 w-12 rounded object-cover ring-1 ring-slate-200 dark:ring-slate-700 cursor-zoom-in"
                            onClick={() => openPreview(imgSrc)}
                            onError={(e) =>
                              (e.currentTarget.src = "/img/no-image.jpg")
                            }
                          />
                          <div className="min-w-0">
                            <div className="font-medium text-slate-900 dark:text-slate-100 truncate">
                              {event.titre || "—"}
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              {event.type_event} • {event.level_event}
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              {event.start ? formatDate(event.start) : "—"}{" "}
                              {past ? "· Passé" : ""}
                            </div>
                          </div>
                          <div className="ml-auto flex flex-col items-end gap-2">
                            <VisibilitySwitch
                              checked={!!event.is_active}
                              onChange={() => handleToggleActive(event)}
                              titleOn="Affiché sur le site"
                              titleOff="Masqué du site"
                            />

                            <VisibilitySwitch
                              checked={!!Number(event.can_register ?? 1)}
                              onChange={() => handleToggleRegister(event)}
                              titleOn="Inscriptions ouvertes"
                              titleOff="Inscriptions closes"
                              onBgClass="bg-blue-600"
                              disabled={past} // optionnel
                            />
                          </div>
                        </div>

                        {event.cate_event && (
                          <div className="mt-2 text-[11px] italic opacity-70">
                            {event.cate_event}
                          </div>
                        )}

                        <div className="mt-3 flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openDetailsModal(event)}
                            className="w-8 h-8 flex items-center justify-center text-white rounded-md bg-blue-600 p-2 shadow-sm transition duration-300 ease-in-out hover:bg-blue-700"
                            title="Détails & participants"
                          >
                            <FaInfoCircle />
                          </button>
                          <button
                            type="button"
                            onClick={() => openGroupMsgModal(event)}
                            disabled={past}
                            className={`w-8 h-8 flex items-center justify-center text-white rounded-md p-2 shadow-sm transition duration-300 ease-in-out ${
                              past
                                ? "bg-slate-400 cursor-not-allowed"
                                : "bg-green-600 hover:bg-green-700"
                            }`}
                            title={
                              past ? "Événement passé" : "Inviter un groupe"
                            }
                          >
                            <FaEnvelope />
                          </button>
                          <ButtonEdit onClick={() => openEditModal(event)} />
                          <ButtonDelete
                            onClick={() => deleteEventFunc(event.id)}
                          />
                        </div>

                        {/* Upload photo (mobile) */}
                        <div className="mt-3">
                          <label
                            htmlFor={`file-m-${event.id}`}
                            className="inline-flex items-center gap-2 text-xs underline cursor-pointer"
                          >
                            <FaImage /> Changer la photo
                          </label>
                          <input
                            id={`file-m-${event.id}`}
                            type="file"
                            accept="image/*"
                            className="sr-only"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleRowImageChange(event.id, file);
                            }}
                          />
                          {uploadingIds[event.id] && (
                            <div className="text-xs mt-1 opacity-70">
                              Upload…
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Barre pagination (bas) */}
                <div className="mt-4 flex items-center justify-center gap-2 text-sm">
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={page === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    title="Page précédente"
                  >
                    <FaChevronLeft />
                  </button>
                  <span className="px-2">
                    Page {page} / {totalPages}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={page === totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    title="Page suivante"
                  >
                    <FaChevronRight />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <ToastContainer />

      {/* Modales */}
      <EditEventModal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        event={editableEvent}
        refreshList={fetchEvents}
      />
      <CreateEventModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        refreshList={fetchEvents}
      />
      <EventDetailsModalEnseignants
        event={detailsEvent}
        adherents={adherents}
        isOpen={!!detailsEvent}
        onClose={closeDetailsModal}
      />
      <GroupMessageModal
        isOpen={isGroupMsgOpen}
        onClose={closeGroupMsgModal}
        event={eventForGroupMsg}
        senderId={user?.userId}
      />

      {/* Modale Upload Calendrier */}
      <UploadEventsModal
        isOpen={isUploadModalOpen}
        onClose={closeUploadModal}
        onUploaded={fetchEvents}
      />

      {/* 🖼️ Modale Preview Image plein écran */}
      {isPreviewOpen && (
        <div
          className="fixed inset-0 z-[120] bg-black/90 flex items-center justify-center p-4"
          onClick={(e) => e.currentTarget === e.target && closePreview()}
          role="dialog"
          aria-modal="true"
          aria-label="Aperçu de l'image"
        >
          <button
            className="absolute top-4 right-4 h-10 w-10 grid place-items-center rounded-full bg-white/15 hover:bg-white/25 text-white"
            onClick={closePreview}
            title="Fermer"
          >
            <FaTimes />
          </button>
          <img
            src={previewSrc}
            alt="Aperçu événement"
            className="max-h-[90vh] max-w-[95vw] object-contain"
            onError={(e) => (e.currentTarget.src = "/img/no-image.jpg")}
          />
        </div>
      )}
    </section>
  );
};

export default EventsPage;
